import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { supabase } from '@/integrations/supabase/client';
import { lovable } from '@/integrations/lovable/index';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { z } from 'zod';
import { Eye, EyeOff } from 'lucide-react';
import { isDespiaApp } from '@/utils/despia';
import { AnimatedBrandIcon } from '@/components/AnimatedBrandIcon';

// Google OAuth is blocked inside embedded webviews (Despia / WKWebView),
// so we hide the button there and let users sign in with email/password.
const hideGoogleSignIn = isDespiaApp();
// Logo is now in public folder for LCP preloading - WebP for smaller size
const philsConversationLogoWebp = '/logo.webp';
const philsConversationLogoPng = '/logo.png';

// Strong password schema
const passwordSchema = z.string()
  .min(8, "Password must be at least 8 characters")
  .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
  .regex(/[a-z]/, "Password must contain at least one lowercase letter")
  .regex(/[0-9]/, "Password must contain at least one number");

const signUpSchema = z.object({
  email: z.string().email("Invalid email format").max(255, "Email too long"),
  password: passwordSchema,
  confirmPassword: z.string(),
  firstName: z.string().min(1, "First name is required").max(50, "First name too long"),
  lastName: z.string().max(50, "Last name too long").optional().or(z.literal('')),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords do not match",
  path: ["confirmPassword"]
});

const signInSchema = z.object({
  email: z.string().email("Invalid email format"),
  password: z.string().min(1, "Password is required")
});

const Auth = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  
  const [isLoading, setIsLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showVerificationPending, setShowVerificationPending] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  const { signIn, signUp, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Preserve a same-origin `next` path so external flows (e.g. MCP OAuth consent
  // at /.lovable/oauth/consent) can send the user back after sign-in.
  const rawNext = searchParams.get("next") ?? "";
  const nextPath = (() => {
    if (!rawNext.startsWith("/") || rawNext.startsWith("//") || /[\\\s\x00-\x1f]/.test(rawNext)) return "/";
    try {
      const u = new URL(rawNext, window.location.origin);
      return u.origin === window.location.origin ? u.pathname + u.search + u.hash : "/";
    } catch {
      return "/";
    }
  })();

  // Skip welcome screen for password reset flow; auto-dismiss after 2.5s
  useEffect(() => {
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    if (hashParams.get('type') === 'recovery' || user) {
      setShowWelcome(false);
      return;
    }
    const timer = setTimeout(() => setShowWelcome(false), 1500);
    return () => clearTimeout(timer);
  }, [user]);

  useEffect(() => {
    // Check for password recovery event
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setShowResetPassword(true);
        setShowForgotPassword(false);
      }
    });

    // Also check URL hash for recovery token (Supabase uses hash fragments)
    const hashParams = new URLSearchParams(window.location.hash.substring(1));
    const type = hashParams.get('type');
    if (type === 'recovery') {
      setShowResetPassword(true);
      setShowForgotPassword(false);
    }

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    // Only redirect if user is logged in AND not in password reset flow
    if (user && !showResetPassword) {
      if (nextPath !== "/") {
        window.location.href = nextPath;
      } else {
        navigate("/");
      }
    }
  }, [user, navigate, showResetPassword, nextPath]);

  const handleSignIn = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate with Zod
    const validationResult = signInSchema.safeParse({ email, password });
    if (!validationResult.success) {
      const firstError = validationResult.error.issues[0];
      toast.error(firstError.message);
      return;
    }

    setIsLoading(true);

    const { error } = await signIn(email, password, rememberMe);

    if (error) {
      // Check for email not confirmed error, show verification screen with resend
      if (error.message?.toLowerCase().includes('email not confirmed')) {
        setShowVerificationPending(true);
      } else {
        toast.error(error.message || 'Failed to sign in');
      }
    } else {
      toast.success('Welcome back!');
      if (nextPath !== "/") {
        window.location.href = nextPath;
      } else {
        navigate("/");
      }
    }

    setIsLoading(false);
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const validationResult = signUpSchema.safeParse({ 
      email, 
      password, 
      confirmPassword, 
      firstName,
      lastName,
    });
    
    if (!validationResult.success) {
      const firstError = validationResult.error.issues[0];
      toast.error(firstError.message);
      return;
    }
    
    setIsLoading(true);

    const { error } = await signUp(email, password, firstName, lastName);

    if (error) {
      toast.error(error.message || 'Failed to sign up');
    } else {
      setShowVerificationPending(true);
    }

    setIsLoading(false);
  };

  const handleResendVerification = async () => {
    if (!email) {
      toast.error('Please enter your email address');
      return;
    }
    
    setIsLoading(true);
    
    const { error } = await supabase.auth.resend({
      type: 'signup',
      email: email,
      options: {
        emailRedirectTo: `${window.location.origin}/auth`
      }
    });
    
    if (error) {
      toast.error(error.message || 'Failed to resend verification email');
    } else {
      toast.success('Verification email sent! Check your inbox.');
    }
    
    setIsLoading(false);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    const { data, error } = await supabase.functions.invoke('send-branded-password-reset', {
      body: { email, redirectTo: `${window.location.origin}/auth` },
    });

    if (error || (data && (data as any).error)) {
      toast.error('Failed to send reset email. Please try again.');
    } else {
      toast.success('Password reset email sent! Check your inbox.');
      setShowForgotPassword(false);
    }

    setIsLoading(false);
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate new password
    const passwordValidation = passwordSchema.safeParse(newPassword);
    if (!passwordValidation.success) {
      toast.error(passwordValidation.error.issues[0].message);
      return;
    }

    if (newPassword !== confirmNewPassword) {
      toast.error('Passwords do not match');
      return;
    }

    setIsLoading(true);

    const { error } = await supabase.auth.updateUser({ password: newPassword });

    if (error) {
      toast.error(error.message || 'Failed to reset password');
    } else {
      toast.success('Password updated successfully! You can now sign in.');
      setShowResetPassword(false);
      setNewPassword('');
      setConfirmNewPassword('');
      // Sign out to clear the recovery session
      await supabase.auth.signOut();
    }

    setIsLoading(false);
  };

  return (
    <main className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-secondary to-accent-light/20 p-4 overflow-hidden relative">
      {/* Welcome splash, full-screen centered overlay with darker bg */}
      <div
        className={`fixed inset-0 z-50 flex flex-col items-center justify-center bg-gradient-to-br from-primary/20 via-primary/12 to-secondary/30 transition-all duration-700 ease-in-out ${
          showWelcome
            ? 'opacity-100'
            : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="text-center px-6">
          <div className="inline-flex items-center justify-center mb-6 animate-scale-in">
            <AnimatedBrandIcon className="block h-28 w-28 sm:h-32 sm:w-32" active={showWelcome} priority />
          </div>
          <div className="text-4xl font-bold text-foreground mb-4 animate-fade-in" style={{ animationDelay: '0.3s', animationFillMode: 'both' }}>
            The Best Practice App
          </div>
          <div className="flex items-center justify-center gap-3 mb-4 animate-fade-in" style={{ animationDelay: '0.6s', animationFillMode: 'both' }}>
            <div className="h-px w-12 bg-primary/40"></div>
            <div className="h-2 w-2 rounded-full bg-primary/60 animate-pulse"></div>
            <div className="h-px w-12 bg-primary/40"></div>
          </div>
          <p className="text-muted-foreground text-xl font-medium animate-fade-in" style={{ animationDelay: '0.9s', animationFillMode: 'both' }}>
            Bridge the gap between your English and Life
          </p>
        </div>
      </div>

      <div className="w-full max-w-md">

        {/* Header (visible after welcome fades) */}
        <div
          className={`text-center mb-8 transition-all duration-700 delay-300 ease-in-out ${
            showWelcome ? 'opacity-0 max-h-0 overflow-hidden mb-0' : 'opacity-100 max-h-[300px]'
          }`}
        >
          <div className="inline-flex items-center justify-center mb-4">
            <picture>
              <source srcSet={philsConversationLogoWebp} type="image/webp" />
              <img 
                src={philsConversationLogoPng} 
                alt="The Best Practice App Logo" 
                className="h-16 w-auto"
                width={182}
                height={64}
              />
            </picture>
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-3">
            The Best Practice App
          </h1>
          <div className="flex items-center justify-center gap-2 mb-3">
            <div className="h-px w-8 bg-primary/30"></div>
            <div className="h-1.5 w-1.5 rounded-full bg-primary/50"></div>
            <div className="h-px w-8 bg-primary/30"></div>
          </div>
          <p className="text-muted-foreground text-lg font-medium">
            Bridge the gap between your English and Life
          </p>
        </div>

        <Card className={`shadow-medium transition-all duration-700 ease-in-out ${
          showWelcome ? 'opacity-0 translate-y-8' : 'opacity-100 translate-y-0 delay-500'
        }`}>
          <CardHeader>
            <CardTitle>
              {showResetPassword ? 'Set New Password' : 'Welcome'}
            </CardTitle>
            <CardDescription>
              {showResetPassword 
                ? 'Enter your new password below' 
                : showForgotPassword 
                  ? 'Reset your password' 
                  : 'Sign in to your account or create a new one'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {showVerificationPending ? (
              <div className="space-y-4 text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-foreground">Check your email</h3>
                <p className="text-muted-foreground">
                  We've sent a verification link to <strong>{email}</strong>. 
                  Please click the link to verify your account.
                </p>

                <div className="text-left bg-secondary/50 rounded-lg p-3 space-y-1.5">
                  <p className="text-sm font-medium text-foreground">Didn't receive it?</p>
                  <ul className="text-xs text-muted-foreground space-y-1 list-disc list-inside">
                    <li>Check your spam or junk folder</li>
                    <li>iCloud, Hotmail & Outlook may delay delivery</li>
                    <li>Make sure the email address above is correct</li>
                    <li>Verification links expire after 24 hours</li>
                  </ul>
                </div>

                <div className="pt-2 space-y-2">
                  <Button
                    variant="outline"
                    className="w-full"
                    onClick={handleResendVerification}
                    disabled={isLoading}
                  >
                    {isLoading ? 'Sending...' : 'Resend verification email'}
                  </Button>
                  <Button
                    variant="ghost"
                    className="w-full"
                    onClick={() => {
                      setShowVerificationPending(false);
                    }}
                  >
                    Wrong email? Go back and try again
                  </Button>
                </div>
              </div>
            ) : showResetPassword ? (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <div className="relative">
                    <Input
                      id="new-password"
                      type={showNewPassword ? "text" : "password"}
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={8}
                      className="pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      aria-label={showNewPassword ? "Hide password" : "Show password"}
                    >
                      {showNewPassword ? (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Must be at least 8 characters with uppercase, lowercase, and number
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-new-password">Confirm New Password</Label>
                  <div className="relative">
                    <Input
                      id="confirm-new-password"
                      type={showConfirmNewPassword ? "text" : "password"}
                      value={confirmNewPassword}
                      onChange={(e) => setConfirmNewPassword(e.target.value)}
                      required
                      minLength={8}
                      className="pr-10"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                      onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                      aria-label={showConfirmNewPassword ? "Hide password" : "Show password"}
                    >
                      {showConfirmNewPassword ? (
                        <EyeOff className="h-4 w-4 text-muted-foreground" />
                      ) : (
                        <Eye className="h-4 w-4 text-muted-foreground" />
                      )}
                    </Button>
                  </div>
                </div>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? 'Updating...' : 'Update Password'}
                </Button>
              </form>
            ) : showForgotPassword ? (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-email">Email</Label>
                  <Input
                    id="reset-email"
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  className="w-full"
                  onClick={() => setShowForgotPassword(false)}
                >
                  Back to Sign In
                </Button>
              </form>
            ) : (
              <Tabs defaultValue="signin" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="signin">Sign In</TabsTrigger>
                  <TabsTrigger value="signup">Sign Up</TabsTrigger>
                </TabsList>

                <TabsContent value="signin">
                  <form onSubmit={handleSignIn} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="signin-email">Email</Label>
                      <Input
                        id="signin-email"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signin-password">Password</Label>
                      <div className="relative">
                        <Input
                          id="signin-password"
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          className="pr-10"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          )}
                        </Button>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Checkbox 
                        id="remember-me" 
                        checked={rememberMe}
                        onCheckedChange={(checked) => setRememberMe(checked === true)}
                      />
                      <Label 
                        htmlFor="remember-me" 
                        className="text-sm font-normal cursor-pointer"
                      >
                        Keep me signed in
                      </Label>
                    </div>
                    <Button type="submit" className="w-full" disabled={isLoading}>
                      {isLoading ? 'Signing in...' : 'Sign In'}
                    </Button>

                    {!hideGoogleSignIn && (
                      <>
                        <div className="relative my-2">
                          <div className="absolute inset-0 flex items-center">
                            <span className="w-full border-t" />
                          </div>
                          <div className="relative flex justify-center text-xs uppercase">
                            <span className="bg-card px-2 text-muted-foreground">or</span>
                          </div>
                        </div>

                        <Button
                          type="button"
                          variant="outline"
                          className="w-full"
                          disabled={isLoading}
                          onClick={async () => {
                            setIsLoading(true);
                            const { error } = await lovable.auth.signInWithOAuth("google", {
                              // Use the public /auth/callback page so ProtectedRoute
                              // can't bounce the user back to /auth before Supabase
                              // finishes hydrating the OAuth session.
                              redirect_uri: `${window.location.origin}/auth/callback`,
                            });
                            if (error) {
                              toast.error('Google sign-in failed. Please try again.');
                            }
                            setIsLoading(false);
                          }}
                        >
                          <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                          </svg>
                          Continue with Google
                        </Button>
                        <p className="text-xs text-muted-foreground text-center">
                          Notice: Signing in with a different email address will create a new account
                        </p>
                      </>
                    )}

                    <Button
                      type="button"
                      variant="link"
                      className="w-full"
                      onClick={() => setShowForgotPassword(true)}
                    >
                      Forgot Password?
                    </Button>
                  </form>
                </TabsContent>

                <TabsContent value="signup">
                  <form onSubmit={handleSignUp} className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label htmlFor="signup-firstname">First Name</Label>
                        <Input
                          id="signup-firstname"
                          type="text"
                          placeholder="John"
                          value={firstName}
                          onChange={(e) => setFirstName(e.target.value)}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="signup-lastname">Last Name</Label>
                        <Input
                          id="signup-lastname"
                          type="text"
                          placeholder="Doe or D."
                          value={lastName}
                          onChange={(e) => setLastName(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-email">Email</Label>
                      <Input
                        id="signup-email"
                        type="email"
                        placeholder="you@example.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-password">Password</Label>
                      <div className="relative">
                        <Input
                          id="signup-password"
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          minLength={8}
                          className="pr-10"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          )}
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Must be at least 8 characters with uppercase, lowercase, and number
                      </p>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-confirm-password">Repeat Password</Label>
                      <div className="relative">
                        <Input
                          id="signup-confirm-password"
                          type={showConfirmPassword ? "text" : "password"}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          required
                          minLength={8}
                          className="pr-10"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                        >
                          {showConfirmPassword ? (
                            <EyeOff className="h-4 w-4 text-muted-foreground" />
                          ) : (
                            <Eye className="h-4 w-4 text-muted-foreground" />
                          )}
                        </Button>
                      </div>
                    </div>
                    <Button type="submit" className="w-full" disabled={isLoading}>
                      {isLoading ? 'Creating account...' : 'Create Account'}
                    </Button>

                    {!hideGoogleSignIn && (
                      <>
                        <div className="relative my-2">
                          <div className="absolute inset-0 flex items-center">
                            <span className="w-full border-t" />
                          </div>
                          <div className="relative flex justify-center text-xs uppercase">
                            <span className="bg-card px-2 text-muted-foreground">or</span>
                          </div>
                        </div>

                        <Button
                          type="button"
                          variant="outline"
                          className="w-full"
                          disabled={isLoading}
                          onClick={async () => {
                            setIsLoading(true);
                            const { error } = await lovable.auth.signInWithOAuth("google", {
                              // Use the public /auth/callback page so ProtectedRoute
                              // can't bounce the user back to /auth before Supabase
                              // finishes hydrating the OAuth session.
                              redirect_uri: `${window.location.origin}/auth/callback`,
                            });
                            if (error) {
                              toast.error(error.message || "Google sign-in failed");
                            }
                            setIsLoading(false);
                          }}
                        >
                          <svg className="h-4 w-4 mr-2" viewBox="0 0 24 24">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                          </svg>
                          Continue with Google
                        </Button>
                      </>
                    )}

                    <p className="text-xs text-center text-muted-foreground mt-4">
                      Mit der Anmeldung akzeptierst du unsere{' '}
                      <Link to="/terms" className="underline hover:text-foreground">
                        AGB
                      </Link>{' '}
                      und{' '}
                      <Link to="/privacy" className="underline hover:text-foreground">
                        Datenschutzerklärung
                      </Link>
                    </p>
                  </form>
                </TabsContent>
              </Tabs>
            )}
          </CardContent>
        </Card>
      </div>
    </main>
  );
};

export default Auth;
